/**
 * <multi-select-filter>
 *
 * Generic accordion-style multi-select filter. Groups of checkbox options
 * organized as collapsible tabs. Emits:
 *   - `ms-filter:change`  { selections }  on every checkbox change
 *   - `ms-filter:apply`   { selections }  when Apply button is clicked
 *   - `ms-filter:reset`   { }             when Reset button is clicked
 *
 * Expected DOM:
 *   <multi-select-filter>
 *     <div class="ms-filter__head">
 *       <h2 class="ms-filter__title">Title</h2>
 *       <span data-ms-counter>X of Y label</span>
 *     </div>
 *     <div class="ms-filter__tabs">
 *       <button class="ms-filter__tab" data-ms-tab="<groupKey>">...</button>
 *       ...
 *     </div>
 *     <div class="ms-filter__panels">
 *       <div class="ms-filter__panel" data-ms-panel="<groupKey>">
 *         <label class="ms-filter__option">
 *           <input type="checkbox" data-ms-input data-group="<groupKey>" value="...">
 *           ...
 *         </label>
 *       </div>
 *     </div>
 *     <div class="ms-filter__footer">
 *       <button data-ms-reset>Reset</button>
 *       <button data-ms-apply>Apply</button>
 *     </div>
 *   </multi-select-filter>
 */
class MultiSelectFilter extends HTMLElement {
	connectedCallback() {
		this.tabs = Array.from(this.querySelectorAll('[data-ms-tab]'));
		this.panels = Array.from(this.querySelectorAll('[data-ms-panel]'));
		this.inputs = Array.from(this.querySelectorAll('[data-ms-input]'));
		this.resetBtn = this.querySelector('[data-ms-reset]');
		this.applyBtn = this.querySelector('[data-ms-apply]');
		this.counterEl = this.querySelector('[data-ms-counter]');
		this.counterTemplate = this.dataset.counterTemplate || '{matched} of {total} {label}';
		this.counterLabel = this.dataset.counterLabel || '';

		this.tabs.forEach((tab) => {
			tab.addEventListener('click', () => this.toggleTab(tab.dataset.msTab));
		});
		this.inputs.forEach((input) => {
			input.addEventListener('change', () => this.emitChange());
		});
		this.resetBtn?.addEventListener('click', () => this.reset());
		this.applyBtn?.addEventListener('click', () => {
			this.dispatchEvent(
				new CustomEvent('ms-filter:apply', {
					detail: { selections: this.getSelections() },
					bubbles: true,
				}),
			);
			this.collapseAll();
		});

		this.handleOutsideClick = (e) => {
			if (!this.tabs.some((t) => t.classList.contains('is-open'))) return;
			if (e.target.closest('[data-ms-tab]') || e.target.closest('[data-ms-dropdown]')) return;
			this.collapseAll();
		};
		document.addEventListener('click', this.handleOutsideClick);
	}

	disconnectedCallback() {
		if (this.handleOutsideClick) document.removeEventListener('click', this.handleOutsideClick);
	}

	toggleTab(key) {
		const tab = this.tabs.find((t) => t.dataset.msTab === key);
		if (!tab) return;
		const isOpen = tab.classList.contains('is-open');
		this.tabs.forEach((t) => {
			t.classList.remove('is-open');
			t.setAttribute('aria-expanded', 'false');
		});
		this.panels.forEach((p) => p.classList.add('is-hidden'));
		if (!isOpen) {
			tab.classList.add('is-open');
			tab.setAttribute('aria-expanded', 'true');
			const panel = this.panels.find((p) => p.dataset.msPanel === key);
			panel?.classList.remove('is-hidden');
		}
	}

	collapseAll() {
		this.tabs.forEach((t) => {
			t.classList.remove('is-open');
			t.setAttribute('aria-expanded', 'false');
		});
		this.panels.forEach((p) => p.classList.add('is-hidden'));
	}

	getSelections() {
		const result = {};
		this.inputs.forEach((input) => {
			const group = input.dataset.group;
			if (!group) return;
			if (!result[group]) result[group] = [];
			if (input.checked) result[group].push(input.value);
		});
		return result;
	}

	setSelections(selections = {}, { silent = true } = {}) {
		this.inputs.forEach((input) => {
			const group = input.dataset.group;
			const values = selections[group];
			const shouldCheck = Array.isArray(values) && values.includes(input.value);
			if (input.checked !== shouldCheck) input.checked = shouldCheck;
		});
		if (!silent) this.emitChange();
	}

	reset() {
		this.inputs.forEach((input) => {
			input.checked = false;
		});
		this.dispatchEvent(
			new CustomEvent('ms-filter:reset', {
				detail: { selections: this.getSelections() },
				bubbles: true,
			}),
		);
		this.emitChange();
	}

	emitChange() {
		this.dispatchEvent(
			new CustomEvent('ms-filter:change', {
				detail: { selections: this.getSelections() },
				bubbles: true,
			}),
		);
	}

	updateCounter(matched, total, label) {
		if (!this.counterEl) return;
		const l = label ?? this.counterLabel;
		this.counterEl.textContent = this.counterTemplate
			.replace('{matched}', matched)
			.replace('{total}', total)
			.replace('{label}', l);
	}

	hasAnySelection() {
		return this.inputs.some((i) => i.checked);
	}
}

if (!customElements.get('multi-select-filter')) {
	customElements.define('multi-select-filter', MultiSelectFilter);
}
